/**
 * THE MANAGER'S SCOPE — the period and the branch every one of his screens is
 * read through, and the formatters that keep the figures in one house style.
 *
 * Two things the boards assume and the backend does not have, recorded here
 * once rather than re-argued on each screen:
 *
 *  - THE BRANCH. `maie-desktop-clinic-manager` draws "Maadi" and "Nasr City"
 *    as first-class rows. There is no branch model in the backend — no table,
 *    no column on an appointment or an invoice, and `/me/clinic-shape` returns
 *    a hardcoded `branches: 1`. So a branch here is the CLINIC, read from the
 *    session's tenant, and every branch dimension is a one-row dimension until
 *    the model lands. Inventing two named branches to fill the layout would be
 *    fabricating data (BRIEF §4.9); reporting the clinic it actually has is
 *    not. Logged in reports/clinic-manager.md as a BACKEND-GAP.
 *
 *  - CALENDAR DAYS ARE STRINGS. `invoice.date` and `appointment.date` are
 *    Postgres `date` columns and arrive as `YYYY-MM-DD` (BRIEF §4.6). They are
 *    compared as strings throughout; the only place a Date is built is to walk
 *    a month, and that is done in local time so "today" is the user's today.
 */

export type DayScope = 'today' | 'week' | 'month';

/** Local calendar day as `YYYY-MM-DD` — never `toISOString()`, which is UTC. */
export function ymd(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function todayYmd(): string {
  return ymd(new Date());
}

export interface Range {
  start: string;
  end: string;
}

/** The whole month `monthKey` ("2026-08") covers, inclusive. */
export function monthRange(monthKey: string): Range {
  const [y, m] = monthKey.split('-').map(Number);
  return { start: `${monthKey}-01`, end: ymd(new Date(y, m, 0)) };
}

/**
 * MONTH NAMES ARE FIXED, NOT LOCALISED.
 *
 * `toLocaleDateString('en-GB', { month: 'short' })` returns "Sept" for
 * September on V8 — four letters where every other month gets three, so the
 * board's "Aug 2026" chip becomes "Sept 2026" and the row stops lining up. The
 * canvases print three letters throughout, so the table is the source.
 */
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'] as const;
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;
const DAYS_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday',
  'Friday', 'Saturday'] as const;

/** "2026-08" → "August 2026". */
export function monthLabel(monthKey: string): string {
  const [y, m] = monthKey.split('-').map(Number);
  return `${MONTHS_LONG[m - 1]} ${y}`;
}

/** "2026-08" → "Aug 2026", the chip's label. */
export function monthChip(monthKey: string): string {
  const [y, m] = monthKey.split('-').map(Number);
  return `${MONTHS_SHORT[m - 1]} ${y}`;
}

/** "2026-07" → "July" — the month's own name, without its year. */
export function monthName(monthKey: string): string {
  const m = Number(monthKey.split('-')[1]);
  return MONTHS_LONG[m - 1];
}

/** "Sunday 6 Sep 2026" — the board's date line, comma-free. */
export function longDay(d: Date): string {
  return `${DAYS_LONG[d.getDay()]} ${d.getDate()} ${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`;
}

/** "Aug 1" — a day inside its own month, the axis label the charts use. */
export function monthDay(day: string): string {
  const [, m, d] = day.split('-').map(Number);
  return `${MONTHS_SHORT[m - 1]} ${d}`;
}

/**
 * "26 Aug" — a calendar day inside a month already named elsewhere on screen.
 *
 * NOT `monthDay`, which is the CHART AXIS formatter and prints "Aug 26": the
 * axis reads left-to-right in months and the tables read day-then-month, the
 * way every other date on this surface is written.
 */
export function dayNoYear(day: string): string {
  const [, m, d] = day.split('-').map(Number);
  return `${d} ${MONTHS_SHORT[m - 1]}`;
}

/** "6 Sep 2026" from a `YYYY-MM-DD` calendar day, read as a local day. */
export function dayLabel(day: string): string {
  const [y, m, d] = day.split('-').map(Number);
  return `${d} ${MONTHS_SHORT[m - 1]} ${y}`;
}

/** "Thursday 28" — the weekday a day of the current month falls on. */
export function weekdayDay(day: string): string {
  const [y, m, d] = day.split('-').map(Number);
  return `${DAYS_LONG[new Date(y, m - 1, d).getDay()]} ${d}`;
}

/** "Sunday 6 Sep 2026" from a `YYYY-MM-DD` calendar day. */
export function longDayLabel(day: string): string {
  const [y, m, d] = day.split('-').map(Number);
  return longDay(new Date(y, m - 1, d));
}

export function shiftMonth(monthKey: string, by: number): string {
  const [y, m] = monthKey.split('-').map(Number);
  const d = new Date(y, m - 1 + by, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * The month a monthly report opens on: the LAST COMPLETE one.
 *
 * A report of a month still being lived is a partial month presented as a
 * month, and "▲ 13% vs July" computed off nine days of September is a lie with
 * a percent sign on it. The current month is one chip away.
 */
export function lastCompleteMonth(now = new Date()): string {
  const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function currentMonth(now = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/** The days of a month, as `YYYY-MM-DD`, for the per-day bars. */
export function daysOfMonth(monthKey: string): string[] {
  const { start, end } = monthRange(monthKey);
  const last = Number(end.slice(8));
  return Array.from({ length: last }, (_, i) => `${start.slice(0, 8)}${String(i + 1).padStart(2, '0')}`);
}

/** The range a day-scope covers, ending today. */
export function dayScopeRange(scope: DayScope, now = new Date()): Range {
  const end = ymd(now);
  if (scope === 'today') return { start: end, end };
  if (scope === 'week') {
    const s = new Date(now);
    s.setDate(s.getDate() - 6);
    return { start: ymd(s), end };
  }
  return { start: `${ymd(now).slice(0, 7)}-01`, end };
}

export function dayScopeLabel(scope: DayScope, now = new Date()): string {
  if (scope === 'today') return longDay(now);
  if (scope === 'week') return 'the last 7 days';
  return monthLabel(currentMonth(now));
}

/* ---------- formatters — the board's number shapes ---------- */

/** "6,400" — grouped, no decimals. The boards never print piastres. */
export function num(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}

/** "EGP 10,550". */
export function money(n: number, currency = 'EGP'): string {
  return `${currency} ${num(n)}`;
}

/** "7:41 pm" from a `HH:MM:SS` time column. */
export function clock(time: string): string {
  const [h, m] = time.split(':').map(Number);
  const suffix = h >= 12 ? 'pm' : 'am';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, '0')} ${suffix}`;
}

/** "28 Aug 6:58 pm" — the audit log's when. */
export function stamp(iso: string): string {
  const d = new Date(iso);
  return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]} `
    + clock(`${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}:00`);
}

/** "today" · "yesterday" · "2 h" · "3 d" — the alert list's when. */
export function since(iso: string, now = new Date()): string {
  const then = new Date(iso);
  const mins = Math.max(0, Math.round((now.getTime() - then.getTime()) / 60000));
  if (mins < 60) return `${mins} m`;
  const hours = Math.round(mins / 60);
  if (ymd(then) === ymd(now)) return hours < 2 ? `${hours} h` : 'today';
  const days = Math.round((Number(new Date(ymd(now))) - Number(new Date(ymd(then)))) / 86400000);
  if (days === 1) return 'yesterday';
  return `${days} d`;
}

/** "▲ 13%" / "▼ 4%" / "=" against a comparison figure. */
export function delta(now: number, before: number): { arrow: string; tone: 'grn' | 'amb' | 'mut'; pct: number } {
  if (!before) return { arrow: now > 0 ? '▲' : '=', tone: now > 0 ? 'grn' : 'mut', pct: 0 };
  const pct = Math.round(((now - before) / before) * 100);
  if (pct > 0) return { arrow: '▲', tone: 'grn', pct };
  if (pct < 0) return { arrow: '▼', tone: 'amb', pct };
  return { arrow: '=', tone: 'mut', pct: 0 };
}

/** A person's display name from any of the shapes the API sends. */
export function personName(p?: { firstName?: string; lastName?: string } | null): string {
  if (!p) return 'Unknown';
  return [p.firstName, p.lastName].filter(Boolean).join(' ').trim() || 'Unknown';
}

/** "S. Adel" — the audit log's who column. */
export function shortName(p?: { firstName?: string; lastName?: string } | null): string {
  if (!p) return 'Unknown';
  const first = (p.firstName || '').trim();
  const last = (p.lastName || '').trim();
  if (first && last) return `${first[0]}. ${last}`;
  return first || last || 'Unknown';
}

/** "Dr. Hana" — how a provider is named in aggregate. */
export function doctorName(p?: { firstName?: string; lastName?: string } | null): string {
  if (!p) return 'Unassigned';
  const first = (p.firstName || '').trim();
  const last = (p.lastName || '').trim();
  return `Dr. ${first || last || 'Unknown'}`;
}

/* ---------- the canvas spells small counts, it does not print them ---------- */

const WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight',
  'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen',
  'seventeen', 'eighteen', 'nineteen', 'twenty', 'twenty-one', 'twenty-two',
  'twenty-three', 'twenty-four', 'twenty-five', 'twenty-six', 'twenty-seven',
  'twenty-eight', 'twenty-nine', 'thirty', 'thirty-one'] as const;

const ORDINALS = ['zeroth', 'first', 'second', 'third', 'fourth', 'fifth', 'sixth',
  'seventh', 'eighth', 'ninth', 'tenth', 'eleventh', 'twelfth', 'thirteenth',
  'fourteenth', 'fifteenth', 'sixteenth', 'seventeenth', 'eighteenth', 'nineteenth',
  'twentieth', 'twenty-first', 'twenty-second', 'twenty-third', 'twenty-fourth',
  'twenty-fifth', 'twenty-sixth', 'twenty-seventh', 'twenty-eighth', 'twenty-ninth',
  'thirtieth', 'thirty-first'] as const;

/**
 * "six" — a day count in words, the way the canvas writes one ("September has
 * six days in it", "six working days of thirty"). Anything past a month's
 * length is a number again, because "one hundred and four" is not a sentence a
 * report should contain.
 */
export function words(n: number): string {
  return n >= 0 && n < WORDS.length ? WORDS[n] : num(n);
}

/** "sixth" — a day of the month in words ("on the sixth of the month"). */
export function ordinalWords(n: number): string {
  return n >= 0 && n < ORDINALS.length ? ORDINALS[n] : `${num(n)}th`;
}

/** How many days of `monthKey` have happened, counting today. */
export function daysElapsed(monthKey: string, now = new Date()): number {
  if (monthKey !== currentMonth(now)) return daysOfMonth(monthKey).length;
  return now.getDate();
}

/** Whole-number shares that add up to 100 — each one rounded on its own, 68 · 24 ·
 *  9 sat under a legend that said 101%. The largest remainders take the spare points. */
export function wholeShares(values: number[]): number[] {
  const total = values.reduce((a, b) => a + b, 0);
  if (!total) return values.map(() => 0);
  const raw = values.map((v) => (v / total) * 100);
  const out = raw.map(Math.floor);
  let spare = 100 - out.reduce((a, b) => a + b, 0);
  raw
    .map((r, i) => [r - Math.floor(r), i] as const)
    .sort((a, b) => b[0] - a[0])
    .forEach(([, i]) => {
      if (spare > 0) { out[i] += 1; spare -= 1; }
    });
  return out;
}
